import { useEffect, useRef } from 'react'

import { FxButton } from '@/components/fx'
import { keyHelp, type KeyBinding } from '@/components/review'

/**
 * What this screen is for, and every key it binds.
 *
 * The key list is `keyHelp(bindings)` — the same array the layer binds — so
 * the legend cannot document a shortcut that does not exist, nor miss one
 * that does. The one line it adds by hand is Enter, which is not a layer
 * binding at all but the row form's own submit; it is described as what it
 * is ("im Feld"), because a person reading the list has to know why Enter
 * does nothing when their focus is elsewhere.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function BulkHelp({ bindings, onClose }: { bindings: KeyBinding[]; onClose: () => void }) {
  const close = useRef<HTMLButtonElement>(null)
  const dialog = useRef<HTMLDivElement>(null)

  // Escape closes, Tab stays inside, and focus goes back where it came from.
  // The dialog claims `aria-modal`, and without the trap Tab walked into the
  // review rows behind the scrim; without the restore, closing it dropped a
  // keyboard reviewer back at the top of a screen that is keyboard-first.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    close.current?.focus()
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      const node = dialog.current
      if (event.key !== 'Tab' || !node) return
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      previous?.focus?.()
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Hilfe schließen"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-text/50"
      />
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-help-title"
        className="relative flex max-h-full w-full max-w-[520px] flex-col gap-4 overflow-auto rounded-xl bg-surface p-10 shadow-overlay"
      >
        <h2 id="bulk-help-title" className="text-dialog-title">
          Unklarheiten beheben
        </h2>
        <p>
          Hier steht dasselbe Feld aus allen Dokumenten untereinander. Sie sehen den Ausschnitt aus
          dem Scan, daneben, was erkannt wurde. Stimmt der Wert, übernehmen Sie ihn; stimmt er
          nicht, tippen Sie den richtigen darüber.
        </p>
        <p className="text-small text-text-muted">
          Ein Feld steht hier, weil nichts erkannt wurde oder weil die Sicherheit unter dem
          Schwellwert liegt. Der Schwellwert oben gilt nur für diese Ansicht.
        </p>
        <h3 className="text-panel-title">Tastatur</h3>
        <ul className="flex flex-col gap-2">
          <li className="flex items-baseline justify-between gap-4">
            <span>Wert übernehmen und zum nächsten Feld (im Feld)</span>
            <kbd className="rounded-sm bg-fill px-2 py-0.5 text-small font-semibold">Enter</kbd>
          </li>
          {keyHelp(bindings).map((entry) => (
            <li key={entry.id} className="flex items-baseline justify-between gap-4">
              <span className={entry.disabled ? 'text-text-muted' : undefined}>{entry.label}</span>
              <span className="flex shrink-0 gap-1">
                {entry.chords.map((chord, index) => (
                  <span key={index} className="flex gap-1">
                    {chord.map((key) => (
                      <kbd key={key} className="rounded-sm bg-fill px-2 py-0.5 text-small font-semibold">
                        {key}
                      </kbd>
                    ))}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <FxButton ref={close} variant="primary" onClick={onClose} className="self-end">
          Verstanden
        </FxButton>
      </div>
    </div>
  )
}
